import { z } from 'zod';
import { AccountCode, OperatorUsername } from './common.js';
import { CreateOrderInput, OrderDetail, Provodka } from './order.js';

// ─── Публичный API приёма заявок: контракты для внешних интеграций ───
// (API-ключи, публичное создание/чтение заявок, доставка вебхуков).

/**
 * Разрешённые области доступа API-ключа. Проверяются на роутах публичного API
 * (следующая задача — БД/роуты), здесь только контракт названий.
 *
 * `orders:confirm` — право на авто-подтверждение заявки при создании (флаг
 * `confirmAll` в PublicCreateOrderInput). Требуется дополнительно к `orders:create`.
 */
export const API_KEY_SCOPES = ['orders:create', 'orders:read', 'orders:confirm', 'refs:read'] as const;
export const ApiKeyScopeSchema = z.enum(API_KEY_SCOPES);
export type ApiKeyScope = z.infer<typeof ApiKeyScopeSchema>;

/** Типы событий, на которые можно подписать вебхук ключа. */
export const WEBHOOK_EVENT_TYPES = ['order.status.changed', 'order.provodka.confirmed'] as const;
export const WebhookEventTypeSchema = z.enum(WEBHOOK_EVENT_TYPES);
export type WebhookEventType = z.infer<typeof WebhookEventTypeSchema>;

/**
 * Значения по умолчанию, подставляемые при создании заявки через публичный API,
 * если внешний вызывающий их не передал (см. PublicCreateOrderInput ниже — там
 * те же поля optional).
 */
export const ApiKeyDefaultsSchema = z
  .object({
    client: AccountCode.optional(),
    executor: AccountCode.optional(),
    operator: OperatorUsername.optional(),
  })
  .strict();
export type ApiKeyDefaults = z.infer<typeof ApiKeyDefaultsSchema>;

export const CreateApiKeyInput = z.object({
  name: z.string().min(1).max(64),
  scopes: z.array(ApiKeyScopeSchema).min(1),
  defaults: ApiKeyDefaultsSchema.default({}),
  webhookUrl: z.string().url().max(2048).nullish(),
  webhookEvents: z.array(WebhookEventTypeSchema).default([]),
});
export type CreateApiKeyInputT = z.infer<typeof CreateApiKeyInput>;

export const UpdateApiKeyInput = CreateApiKeyInput.partial().extend({
  enabled: z.boolean().optional(),
});
export type UpdateApiKeyInputT = z.infer<typeof UpdateApiKeyInput>;

export const ApiKeyDto = z.object({
  id: z.string().uuid(),
  name: z.string(),
  keyPrefix: z.string(),
  scopes: z.array(ApiKeyScopeSchema),
  defaults: ApiKeyDefaultsSchema,
  webhookUrl: z.string().nullable(),
  webhookEvents: z.array(WebhookEventTypeSchema),
  webhookSecretSet: z.boolean(),
  enabled: z.boolean(),
  revokedAt: z.string().nullable(),
  lastUsedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ApiKeyDtoT = z.infer<typeof ApiKeyDto>;

// plainKey возвращается ровно один раз при создании (хэш — единственное, что остаётся
// в БД); webhookSecret — только если был сгенерирован в рамках этой операции
// (задан webhookUrl, а секрета ранее не было).
export const CreateApiKeyResult = z.object({
  key: ApiKeyDto,
  plainKey: z.string(),
  webhookSecret: z.string().optional(),
});
export type CreateApiKeyResultT = z.infer<typeof CreateApiKeyResult>;

export const UpdateApiKeyResult = z.object({
  key: ApiKeyDto,
  webhookSecret: z.string().optional(),
});
export type UpdateApiKeyResultT = z.infer<typeof UpdateApiKeyResult>;

/**
 * Заявка, создаваемая внешней системой через публичный API. Те же поля и валидации,
 * что у CreateOrderInput, но client/executor/operator необязательны (могут прийти
 * из ApiKeyDefaults ключа) и добавлен externalId — идентификатор заявки на стороне
 * внешней системы (для идемпотентности/сверки, дедуп — забота роутов, не схемы).
 */
export const PublicCreateOrderInput = CreateOrderInput.omit({
  client: true,
  executor: true,
  operator: true,
}).extend({
  client: AccountCode.optional(),
  executor: AccountCode.optional(),
  operator: OperatorUsername.optional(),
  externalId: z.string().min(1).max(128).optional(),
  /**
   * Авто-подтверждение всех 4 проводок сразу при создании (заявка рождается
   * закрытой). Требует у ключа scope `orders:confirm` (иначе 403 — заявка не
   * создаётся). Игнорируется при идемпотентном повторе (заявка уже создана —
   * возвращается как есть). См. createPublicOrder в public-api/service.ts.
   */
  confirmAll: z.boolean().optional(),
});
export type PublicCreateOrderInputT = z.infer<typeof PublicCreateOrderInput>;

/**
 * Урезанная проводка для публичного DTO заявки: только то, что нужно внешнему
 * потребителю для отслеживания исполнения ноги. Без внутренних id/orderId (уже
 * есть в родительском объекте), type (всегда 'obligation'), accountLabel/actionLabel
 * (человекочитаемые дубли) и confirmedBy (внутренняя аудит-ссылка на пользователя).
 */
export const PublicProvodka = Provodka.pick({
  idx: true,
  account: true,
  ccy: true,
  amount: true,
  status: true,
  confirmedAt: true,
});
export type PublicProvodka = z.infer<typeof PublicProvodka>;

/**
 * Публичное представление заявки — то же, что OrderDetail (schemas/order.ts),
 * за вычетом полей, не предназначенных для внешнего потребителя:
 * - profitAmount/profitCcy/profitNativeAmount/profitNativeCcy — профит - server-of-truth
 *   внутренняя бизнес-метрика компании, не дело внешней системы;
 * - usdtRatesSnapshot — внутренний механизм воспроизводимости профита;
 * - deleteReason/reverseEntries — аудит-поля (причина soft-delete и внутренний
 *   double-entry журнал подтверждений);
 * - operator — внутренняя информация (TG-username/email сотрудника).
 * provodki — урезаны до PublicProvodka (см. выше). Добавлено externalId — эхо
 * идентификатора, переданного при создании (null, если создано не через публичный API).
 */
export const PublicOrderDto = OrderDetail.omit({
  profitAmount: true,
  profitCcy: true,
  profitNativeAmount: true,
  profitNativeCcy: true,
  usdtRatesSnapshot: true,
  deleteReason: true,
  reverseEntries: true,
  operator: true,
}).extend({
  provodki: z.array(PublicProvodka),
  externalId: z.string().nullable(),
});
export type PublicOrderDtoT = z.infer<typeof PublicOrderDto>;

/** Строка журнала запросов к публичному API (для UI /admin/integrations). */
export const ApiRequestLogDto = z.object({
  id: z.number(),
  keyId: z.string().uuid().nullable(),
  keyName: z.string().nullable(),
  // Префикс ключа из неудачной попытки аутентификации (ключ не найден/невалиден) —
  // keyId в этом случае недоступен, но префикс полезен для диагностики.
  keyPrefixAttempted: z.string().nullable(),
  method: z.string(),
  path: z.string(),
  statusCode: z.number(),
  orderId: z.string().nullable(),
  externalId: z.string().nullable(),
  errorCode: z.string().nullable(),
  ip: z.string().nullable(),
  durationMs: z.number().nullable(),
  createdAt: z.string(),
});
export type ApiRequestLogDtoT = z.infer<typeof ApiRequestLogDto>;

/** Строка журнала доставки вебхуков (для UI /admin/integrations). */
export const WebhookDeliveryDto = z.object({
  id: z.number(),
  keyId: z.string().uuid(),
  keyName: z.string().nullable(),
  eventType: WebhookEventTypeSchema,
  orderId: z.string(),
  status: z.enum(['pending', 'success', 'failed']),
  attempts: z.number(),
  nextRetryAt: z.string().nullable(),
  lastStatusCode: z.number().nullable(),
  lastError: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  createdAt: z.string(),
});
export type WebhookDeliveryDtoT = z.infer<typeof WebhookDeliveryDto>;
